import React, { useEffect, useState } from 'react';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { GetServerSideProps, NextPageWithLayout } from 'next';
import extractFromContext from '@helpers/utils/extract-from-context';
import { useTranslation } from 'next-i18next';
import { PriceRecommendationInterface, ProductDraftRequestPayload } from '@interfaces/models/deposit';
import DepositService from '@services/deposit-service';
import AuthenticationGuard from '@components/atoms/authentication/authentication-guard/authentication-guard';
import Spinner from '@components/atoms/spinner/spinner';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { useAnalytics } from '@context/analytics.context';
import { useContextSelector } from 'use-context-selector';
import usePreviousState from '@hooks/use-previous-state';
import LearnMoreModal from '@components/organisms/learn-more-modal';
import FormSentence from '@components/organisms/form-sentence';
import { ListingContext } from '@context/listing.context';
import GenericError from '@components/organisms/pricing-calculator-generic-error/pricing-calculator-generic-error';
import Head from '@components/atoms/head/head';
import pricingCalculatorMetas from '@constants/metas/pricing-calculator';
import useLocalStorage from '@hooks/use-local-storage';
import { usePreferences } from '@context/preferences.context';
import logger from '@helpers/utils/logger/client';
import { useQuery } from '@tanstack/react-query';
import { PricingCalculatorQueryKeys } from '@enums/react-query-keys';
import clsx from 'clsx';
import SellLayout from '@layouts/sell-layout';
import { ModalContext } from '@context/modal.context';
import DefaultLayout from '@layouts/default-layout';
import Button from '@components/atoms/button/button';
import useUser from '@hooks/user/use-user';
import { getAppInitialProps } from '@helpers/initial-props';
import styles from './index.module.scss';

type SendEventType = {
  type: string;
  prevState: string;
  currentState: string;
  eventLabel: string;
  snowplowEntityContext: Record<string, string | number>;
};

const PricingCalculator: NextPageWithLayout = () => {
  const { t } = useTranslation('pricing-calculator');
  const { t: tDeposit } = useTranslation('deposit-form');
  const { language } = usePreferences();
  const { updateDataLayer } = useAnalytics();
  const { sendAnalyticEvent } = useAnalyticEvents('pricingCalculator');
  const { isAuthenticated } = useUser();
  const openModal = useContextSelector(ModalContext, (v) => v.openModal);

  const pageMetas = pricingCalculatorMetas[language];

  const [productDraftPayload, setProductDraftPayload] = useLocalStorage<Partial<ProductDraftRequestPayload>>(
    'deferredProductDraftPayload',
    {},
  );

  const {
    categories,
    universes,
    brands,
    models,
    pendingAction,
    listingDraftPayload,
    setListingDraftAttribute,
    createListingDraft,
    setPricingCalculatorInputTouched,
    pricingCalculatorInputTouched,
    brandLabel,
    universeLabel,
    modelLabel,
    categoryLabel,
    modelVariationLabel,
    showDefaultPricingCalculatorMessage,
    setShowDefaultPricingCalculatorMessage,
  } = useContextSelector(ListingContext, (state) => state);

  const [loading, setLoading] = useState<boolean>(false);
  const [draftId, setDraftId] = useState<string>('');
  const [draftCreateError, setDraftCreateError] = useState<boolean>(false);
  const [priceEstimateError, setPriceEstimateError] = useState<boolean>(false);
  const [isNetworkError, setIsNetworkError] = useState<boolean>(false);
  const [predictedPrices, setPredictedPrices] = useState<Partial<PriceRecommendationInterface>>({});
  const [isFromOnAuth, setIsFromOnAuth] = useState<boolean>(false);
  const prevState = usePreviousState(listingDraftPayload);
  const isDisabled = !Boolean(
    listingDraftPayload.brand && listingDraftPayload.universe && listingDraftPayload.category,
  );

  const openLearnMoreModal = () => {
    return openModal(
      <LearnMoreModal
        buyerDisplayPrice={predictedPrices?.avgPriceBuyer?.display}
        sellerDisplayPrice={predictedPrices?.avgPriceSeller?.display}
      />,
      {
        styles: {
          content: {
            maxWidth: '660px',
            width: '100%',
          },
        },
        onAfterOpen: () => {
          sendAnalyticEvent('pricing_calculator_learn_more');
        },
      },
    );
  };

  const sendEvent = ({ type, prevState, currentState, eventLabel, snowplowEntityContext }: SendEventType) => {
    if (prevState !== currentState) {
      sendAnalyticEvent(`pricing_calculator_select_${type}`, {
        eventLabel,
        ...snowplowEntityContext,
      });
    }
  };

  const { refetch: reFetchPriceEstimate } = useQuery<PriceRecommendationInterface>({
    queryKey: [PricingCalculatorQueryKeys.PRICE_ESTIMATE, JSON.stringify(listingDraftPayload)],
    queryFn: () => DepositService.getPriceEstimate(listingDraftPayload),
    onError: () => {
      setIsNetworkError(true);
    },
    enabled: Boolean(listingDraftPayload.brand && listingDraftPayload.universe && listingDraftPayload.category),
    onSuccess: (pricing) => {
      // 204 No-Content (Show error message when no price recommendation from PRIM)
      if (pricing && !Object.keys(pricing).length) {
        setPriceEstimateError(true);
      } else {
        setPredictedPrices(pricing);
      }
    },
    onSettled: (pricing) => {
      if (pricingCalculatorInputTouched) {
        const { brand, universe, category, macroModelId, modelVariationId } = listingDraftPayload;

        const snowplowEntityContext = {
          recommended_price: pricing?.avgPriceBuyer?.display || '',
          selected_universe: universeLabel,
          selected_category: categoryLabel,
          selected_brand: brandLabel,
          selected_macromodel: models.length ? modelLabel : '',
          selected_model_variation: models.length ? modelVariationLabel : '',
        };

        const eventData = [
          {
            type: 'universe',
            prevState: prevState?.universe,
            currentState: universe,
            eventLabel: universeLabel,
          },
          {
            type: 'category',
            prevState: prevState?.category,
            currentState: category,
            eventLabel: categoryLabel,
          },
          {
            type: 'brand',
            prevState: prevState?.brand,
            currentState: brand,
            eventLabel: brandLabel,
          },
          {
            type: 'macromodel',
            prevState: prevState?.macroModelId,
            currentState: macroModelId,
            eventLabel: modelLabel,
          },
          {
            type: 'model_variation',
            prevState: prevState?.modelVariationId,
            currentState: modelVariationId,
            eventLabel: modelVariationLabel,
          },
        ];

        for (const data of eventData) {
          const event = {
            ...data,
            snowplowEntityContext,
          };

          if (['brand', 'universe', 'category'].includes(event.type) && event.prevState !== event.currentState) {
            sendEvent(event);
            break;
          }

          if (['macromodel', 'model_variation'].includes(event.type) && event.eventLabel) {
            sendEvent(event);
          }
        }
      }
    },
  });

  const handleCreateListingDraft = async () => {
    sendAnalyticEvent('pricing_calculator_list_cta');
    if (!isAuthenticated) {
      return;
    }
    setLoading(true);
    try {
      const { draftId } = await createListingDraft('pricing_calculator');
      updateDataLayer({ preduct_id: draftId });
      if (draftId) {
        sendAnalyticEvent('pricing_calculator_draft_creation', {
          preduct_id: draftId,
          recommended_price: predictedPrices?.avgPriceBuyer?.display,
          selected_universe: universeLabel,
          selected_category: categoryLabel,
          selected_brand: brandLabel,
          selected_macromodel: models.length ? modelLabel : '',
          selected_model_variation: models.length ? modelVariationLabel : '',
        });
        setDraftId(draftId);
      }
    } catch (err) {
      logger.info(err, 'ERROR: Unable to create draft, server responded with: ');
      setDraftCreateError(true);
    }
  };

  const handleClickCTA = () => {
    sendAnalyticEvent('pricing_calculator_list_cta');
    // Used so that we don't send the listing events again after authentication
    setPricingCalculatorInputTouched(false);
  };

  const handleAuth = () => {
    setProductDraftPayload(listingDraftPayload);
    setIsFromOnAuth(true);
  };

  useEffect(() => {
    if (isDisabled) {
      setShowDefaultPricingCalculatorMessage(true);
      setPredictedPrices({});
    }
  }, [isDisabled]);

  useEffect(() => {
    if (isAuthenticated && isFromOnAuth) {
      handleCreateListingDraft();
      setIsFromOnAuth(false);
    }
  }, [isAuthenticated, isFromOnAuth]);

  useEffect(() => {
    if (universes.length && brands.length) {
      const { universe, category, brand, macroModelId, modelVariationId } = productDraftPayload;
      setListingDraftAttribute({
        universe: universe || universes[0]?.id,
        category: category || categories[0]?.subCategories[0]?.id,
        brand: brand || brands[0]?.pimId,
        macroModelId: macroModelId || '',
        modelVariationId: modelVariationId || '',
      });
    }
  }, [universes, brands, isAuthenticated]);

  useEffect(() => {
    if (models.length && !productDraftPayload.macroModelId) {
      setListingDraftAttribute({
        macroModelId: '',
      });
    }
  }, [models]);

  useEffect(() => {
    updateDataLayer({
      env_template: 'pricing_calculator',
      pageUrl: '/pricing-calculator',
      screen_category: 'pricing_calculator',
    });
  }, []);

  // Used to reset the network error variable when the user changes the input.
  useEffect(() => {
    if (pricingCalculatorInputTouched) {
      setIsNetworkError(false);
    }
  }, [pricingCalculatorInputTouched]);

  useEffect(() => {
    // When the first request fails, no need to send any additional requests of other payload draft
    if (isNetworkError && !pricingCalculatorInputTouched) {
      return;
    }
    setPriceEstimateError(false);
    const { brand, universe, category } = listingDraftPayload;
    if (!(category && brand && universe)) {
      return;
    }
    reFetchPriceEstimate();
  }, [JSON.stringify(listingDraftPayload)]);

  useEffect(() => {
    if (draftId) {
      setProductDraftPayload({});
      window.location.href = `${window.location.origin}${tDeposit('DEPOSIT_FORM.CONTINUE.URL')}?id=${draftId}`;
    }
  }, [draftId]);

  return (
    <>
      <Head metadata={pageMetas} />
      <main>
        <div className={styles.pricingCalculatorContainer}>
          {pendingAction || loading ? (
            <div className={clsx(styles.pricingCalculator, styles['pricingCalculator--loadingFeature'])}>
              <Spinner />
            </div>
          ) : !draftCreateError ? (
            <div className={styles.pricingCalculator}>
              <section className={styles.pricingCalculator__formSentence}>
                <h1 className={styles.pricingCalculator__title}>{t('PRICING_CALCULATOR.TITLE')}</h1>
                <FormSentence />
              </section>
              <section className={styles.pricingCalculator__estimate}>
                <h2 className={styles.pricingCalculator__estimate__title}>{t('PRICING_CALCULATOR.ESTIMATE.TITLE')}</h2>
                {!priceEstimateError && (
                  <ul className={styles.pricingCalculator__estimate__priceRange}>
                    <li>{predictedPrices?.minPriceBuyer?.display}</li>
                    <li className={styles.highlighted}>{predictedPrices?.avgPriceBuyer?.display}</li>
                    <li>{predictedPrices?.maxPriceBuyer?.display}</li>
                  </ul>
                )}
                <p className={styles.pricingCalculator__estimate__text}>
                  {!pricingCalculatorInputTouched || showDefaultPricingCalculatorMessage ? (
                    <span>{`${t('PRICING_CALCULATOR.ESTIMATE.DESCRIPTION.DEFAULT')} `}</span>
                  ) : predictedPrices?.id && !priceEstimateError ? (
                    <span>{`${t('PRICING_CALCULATOR.ESTIMATE.DESCRIPTION.RESULT')} `}</span>
                  ) : (
                    <span>{`${t('PRICING_CALCULATOR.ESTIMATE.DESCRIPTION.NO_RESULT')} `}</span>
                  )}
                  {!priceEstimateError && (
                    <>
                      {`${t('PRICING_CALCULATOR.ESTIMATE.HOW')} `}
                      <Button
                        onClick={openLearnMoreModal}
                        className={styles.pricingCalculator__estimate__link}
                      >
                        {t('PRICING_CALCULATOR.ESTIMATE.CTA')}
                      </Button>
                    </>
                  )}
                </p>
              </section>
              <div className={styles.pricingCalculator__listingButton}>
                <AuthenticationGuard
                  onClick={handleClickCTA}
                  onAuth={handleAuth}
                >
                  <button
                    className={clsx(
                      'vc-btn vc-btn--primary vc-btn--large vc-btn--full vc-btn--notfull--md-up',
                      styles.pricingCalculator__cta,
                    )}
                    onClick={handleCreateListingDraft}
                    disabled={isDisabled}
                    type="button"
                    id="vc-preduct-add-submit"
                  >
                    {t('PRICING_CALCULATOR.CTA')}
                  </button>
                </AuthenticationGuard>
              </div>
            </div>
          ) : (
            <GenericError />
          )}
        </div>
      </main>
    </>
  );
};

PricingCalculator.Layout = [DefaultLayout, SellLayout];
PricingCalculator.PageType = 'PricingCalculatorPage';

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { locale } = extractFromContext(context);
  const initialProps = await getAppInitialProps(context);
  const translations = await serverSideTranslations(locale, [
    'default-layout',
    'pricing-calculator',
    'deposit-form',
    'common',
  ]);
  return {
    props: {
      ...initialProps,
      ...translations,
    },
  };
};

export default PricingCalculator;
